/**
 * Forgot Password
 *
 * Asks for an address and requests a reset link. The confirmation screen is
 * deliberately identical whether or not the address has an account: the API
 * answers the same way for both, and the UI must not undo that by saying
 * "no account found", which would turn this page into a way to test whether
 * someone is a Farmgate customer.
 */

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, TextField, Button, Alert } from '@mui/material';
import { East, ArrowBack } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useThemeMode, ForceLightTheme } from '../../context/ThemeContext';
import { requestPasswordReset } from '../../services/auth';

function ForgotPasswordInner() {
  const { palette } = useThemeMode();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      // Only transport problems land here; the API itself always accepts.
      setError(err?.message || 'Could not reach Farmgate. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: palette.bg }}>
      <Container maxWidth="xs">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Typography
            component={RouterLink}
            to="/"
            sx={{
              display: 'block', mb: 3, fontWeight: 700, fontSize: '1.05rem',
              color: palette.accent, textDecoration: 'none',
            }}
          >
            Farmgate
          </Typography>

          {sent ? (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: palette.text }}>
                Check your email
              </Typography>
              <Typography sx={{ color: palette.textSecondary, fontSize: '0.9rem', mb: 1.5 }}>
                If <strong>{email.trim()}</strong> has a Farmgate account, a reset link is on its
                way. It is valid for one hour and can be used once.
              </Typography>
              <Typography sx={{ color: palette.textMuted, fontSize: '0.82rem', mb: 3 }}>
                Nothing after a few minutes? Check your spam folder, then try again.
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                disableElevation
                startIcon={<ArrowBack sx={{ fontSize: '16px !important' }} />}
                sx={{ bgcolor: palette.accent, textTransform: 'none', fontWeight: 600, borderRadius: '10px', '&:hover': { bgcolor: '#1C9550' } }}
              >
                Back to sign in
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: palette.text }}>
                Reset your password
              </Typography>
              <Typography sx={{ color: palette.textSecondary, fontSize: '0.9rem', mb: 3 }}>
                Enter the address you sign in with and we will email you a link.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Email" type="email" required fullWidth size="small" autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  InputLabelProps={{ sx: { color: palette.textMuted } }}
                  InputProps={{
                    sx: {
                      color: palette.text, bgcolor: palette.card,
                      '& fieldset': { borderColor: palette.cardBorder },
                      '&:hover fieldset': { borderColor: palette.accent },
                      '&.Mui-focused fieldset': { borderColor: palette.accent },
                    },
                  }}
                />
                <Button
                  type="submit" variant="contained" disableElevation
                  disabled={!email.trim() || submitting}
                  endIcon={<East sx={{ fontSize: '16px !important' }} />}
                  sx={{
                    bgcolor: palette.accent, color: '#fff', fontWeight: 600, py: 1.2,
                    borderRadius: '10px', textTransform: 'none',
                    '&:hover': { bgcolor: '#1C9550' },
                    '&.Mui-disabled': { bgcolor: palette.cardBorder, color: palette.textMuted },
                  }}
                >
                  {submitting ? 'Sending…' : 'Email me a link'}
                </Button>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography
                  component={RouterLink}
                  to="/login"
                  sx={{ fontSize: '0.85rem', color: palette.textSecondary, textDecoration: 'none', '&:hover': { color: palette.accent } }}
                >
                  Back to sign in
                </Typography>
              </Box>
            </Box>
          )}
        </motion.div>
      </Container>
    </Box>
  );
}

function ForgotPassword() {
  return (
    <ForceLightTheme>
      <ForgotPasswordInner />
    </ForceLightTheme>
  );
}

export default ForgotPassword;
